import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";

const lines = ["A ESSÊNCIA", "DO SEU", "TREINO."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 8]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen bg-offwhite overflow-hidden flex items-center pt-24">
      <div className="absolute -top-32 -right-32 w-[42rem] h-[42rem] rounded-full bg-baby/60 blur-3xl pointer-events-none" />
      <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="text-xs uppercase tracking-[0.3em] text-military font-semibold mb-8 flex items-center gap-3"
          >
            <span className="w-10 h-px bg-military inline-block" />
            Suplementação editorial · desde 2021
          </motion.p>
          <h1 className="font-serif text-military-dark leading-[0.92] tracking-tight">
            {lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  data-testid={`hero-line-${i}`}
                  className={`block text-6xl md:text-[8.5vw] lg:text-[7.5vw] ${i === 1 ? "italic font-light text-military" : "font-semibold"}`}
                  initial={{ y: "110%", rotate: 3 }}
                  animate={{ y: 0, rotate: 0 }}
                  transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1], delay: 0.35 + i * 0.14 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.8, ease: "easeOut" }}
            className="mt-8 max-w-md text-base md:text-lg text-military-dark/70 leading-relaxed"
          >
            Proteínas, pré-treinos e vitaminas com curadoria de verdade. Sem ruído, sem excesso — só o que o seu corpo precisa para performar.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.8, ease: "easeOut" }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#catalogo"
              data-testid="hero-cta-catalog"
              onClick={(e) => { e.preventDefault(); window.__lenis?.scrollTo("#catalogo", { offset: -60, duration: 1.4 }); }}
              className="rounded-full bg-military text-offwhite px-8 py-4 text-sm uppercase tracking-widest font-semibold hover:bg-baby hover:text-military-dark transition-colors duration-300"
            >
              Explorar catálogo
            </a>
            <a
              href="#consultora"
              data-testid="hero-cta-ai"
              onClick={(e) => { e.preventDefault(); window.__lenis?.scrollTo("#consultora", { offset: -60, duration: 1.4 }); }}
              className="rounded-full border border-military/30 px-8 py-4 text-sm uppercase tracking-widest font-semibold text-military-dark hover:border-military hover:bg-white transition-colors duration-300 flex items-center gap-2"
            >
              <Sparkles size={16} /> Consultora IA
            </a>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.9, duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
            className="relative"
          >
            <div className="absolute inset-0 spotlight scale-125" />
            <motion.div style={{ y: yImg, rotate }} className="relative overflow-hidden rounded-lg border border-military/10 shadow-[0_40px_80px_rgba(43,54,38,0.15)]">
              <img
                src="https://images.unsplash.com/photo-1664787020182-7c17ea808ae0?w=1000&q=80"
                alt="Pote de suplemento minimalista leve essenza"
                data-testid="hero-product-image"
                className="w-full aspect-[4/5] object-cover"
              />
            </motion.div>
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-10 bottom-16 bg-white/80 backdrop-blur-xl border border-white/40 rounded-full px-6 py-3 shadow-lg"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-military font-semibold">Whey Isolado Pure</p>
              <p className="font-serif italic text-xl text-military-dark">R$ 249,90</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-6 md:left-12 lg:left-24 flex items-center gap-3 text-military-dark/50"
      >
        <ArrowDown size={16} className="animate-bounce" />
        <span className="text-xs uppercase tracking-[0.25em]">role para sentir</span>
      </motion.div>
    </section>
  );
};
